/**
 * PRD.330 Phase 4 — Pure category-bar result shaping.
 *
 * Turns the grouped projection rows (one per category present in the applied
 * query) into the ordered category list a visualization shows:
 *   - declared known categories first, in their declared order;
 *   - a known category absent from the result is shown as zero ONLY when the
 *     applied query does not exclude it (no filter on the dimension, or a
 *     values filter that includes it) — a query filtered to GCash never
 *     reinserts Cash/Card as zero;
 *   - unknown/legacy values are never dropped or merged into a known bucket
 *     (e.g. "Other"); they follow the known categories, ordered by label.
 * Amounts stay raw numbers (minor units); formatting happens at display time.
 */
import type {
  ReportCategoryBarDef,
  ReportColumnFilter,
  ReportVisualizationCategory,
} from './report-types.js';

export interface ReportGroupedRow {
  category: string | null;
  primary: number;
  secondary?: number;
}

export function buildCategoryBarCategories<C extends string>(input: {
  visualization: ReportCategoryBarDef<C>;
  rows: ReadonlyArray<ReportGroupedRow>;
  /** The applied filter on `visualization.dimension`, if any. */
  dimensionFilter: ReportColumnFilter | undefined;
  labelFor: (value: string) => string;
}): ReportVisualizationCategory[] {
  const { visualization, dimensionFilter, labelFor } = input;
  const hasSecondary = visualization.secondaryMeasure !== undefined;
  const byValue = new Map<string, ReportGroupedRow>();
  for (const row of input.rows) byValue.set(row.category ?? '', row);

  const toCategory = (value: string, row: ReportGroupedRow | undefined, known: boolean) => {
    const category: ReportVisualizationCategory = {
      value,
      label: labelFor(value),
      primary: row ? Number(row.primary) || 0 : 0,
      known,
    };
    if (hasSecondary) category.secondary = row ? Number(row.secondary) || 0 : 0;
    return category;
  };

  // Which absent known categories may be zero-filled under the applied query.
  const allowZero = (value: string): boolean => {
    if (dimensionFilter === undefined) return true;
    if (dimensionFilter.kind === 'values') return dimensionFilter.values.includes(value);
    return false; // any other filter on the dimension: show only what matched
  };

  const known = visualization.knownCategories ?? [];
  const knownSet = new Set(known);
  const out: ReportVisualizationCategory[] = [];
  for (const value of known) {
    const row = byValue.get(value);
    if (row || allowZero(value)) out.push(toCategory(value, row, true));
  }

  const unknown = [...byValue.keys()]
    .filter((value) => !knownSet.has(value))
    .map((value) => toCategory(value, byValue.get(value), false))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  return [...out, ...unknown];
}
